// Postep w trybie podrozy: ktore trasy gracz juz zaliczyl.
//
// Osobny klucz od stanu gry (persistence.ts) i rekordow (records.ts), bo postep ma
// wlasny cykl zycia: rzadzi odblokowywaniem tras, a jego reset nie musi kasowac ani
// konstrukcji, ani czasow.
//
// Trasy sa uporzadkowane (CHALLENGES). Pierwsza jest zawsze odblokowana, a kazda
// kolejna odblokowuje sie po zaliczeniu WSZYSTKICH poprzednich (prefiks). Dzieki
// temu nie ma "dziur": nie da sie miec otwartej trzeciej trasy bez drugiej.
//
// localStorage moze byc niedostepny (tryb prywatny, brak zgody). Wtedy zapis jest
// po cichu pomijany - gra dziala dalej, tylko bez pamieci postepu.

import { safeStorage } from './persistence';

export const PROGRESS_KEY = 'robot-lab.progress.v1';

export interface Progress {
  /** Id plansz zaliczonych kiedykolwiek (kolejnosc bez znaczenia). */
  completed: string[];
}

export function emptyProgress(): Progress {
  return { completed: [] };
}

/** Wczytuje postep. Zwraca pusty postep przy braku zapisu lub uszkodzonych danych. */
export function loadProgress(storage: Storage | null = safeStorage()): Progress {
  if (!storage) return emptyProgress();
  let raw: string | null = null;
  try {
    raw = storage.getItem(PROGRESS_KEY);
  } catch {
    return emptyProgress();
  }
  if (!raw) return emptyProgress();

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyProgress();
  }
  if (typeof parsed !== 'object' || parsed === null) return emptyProgress();

  const completed = (parsed as Record<string, unknown>).completed;
  if (!Array.isArray(completed)) return emptyProgress();

  const ids = (completed as unknown[]).filter(
    (id): id is string => typeof id === 'string' && id.length > 0,
  );
  return { completed: [...new Set(ids)] };
}

export function isCompleted(progress: Progress, challengeId: string): boolean {
  return progress.completed.includes(challengeId);
}

/**
 * Zapisuje zaliczenie trasy. Zwraca true tylko przy PIERWSZYM zaliczeniu -
 * powtorki nie zmieniaja postepu (i nie zaśmiecają zapisu).
 */
export function markCompleted(
  progress: Progress,
  challengeId: string,
  storage: Storage | null = safeStorage(),
): boolean {
  if (challengeId === '' || isCompleted(progress, challengeId)) return false;
  progress.completed = [...progress.completed, challengeId];
  saveProgress(progress, storage);
  return true;
}

function saveProgress(progress: Progress, storage: Storage | null): void {
  if (!storage) return;
  try {
    storage.setItem(PROGRESS_KEY, JSON.stringify({ completed: [...progress.completed] }));
  } catch {
    // brak zapisu nie jest bledem gry - postep zostaje w pamieci sesji
  }
}

/**
 * Ile tras jest odblokowanych: dlugosc prefiksu zaliczonych + pierwsza niezaliczona
 * (ta wlasnie gracz ma teraz zrobic). Wartosc z przedzialu 0..order.length.
 */
export function unlockedCount(order: readonly string[], progress: Progress): number {
  let done = 0;
  while (done < order.length) {
    const id = order[done];
    if (id === undefined || !isCompleted(progress, id)) break;
    done += 1;
  }
  return Math.min(order.length, done + 1);
}

/** Czy trase wolno wybrac (pierwsza zawsze, kolejna po zaliczeniu poprzednich). */
export function isUnlocked(
  order: readonly string[],
  progress: Progress,
  challengeId: string,
): boolean {
  const index = order.indexOf(challengeId);
  if (index < 0) return false;
  return index < unlockedCount(order, progress);
}

/** Trasa odblokowana przez ostatnie zaliczenie (null, gdy nic nowego nie otwarto). */
export function newlyUnlocked(
  order: readonly string[],
  before: Progress,
  after: Progress,
): string | null {
  const was = unlockedCount(order, before);
  const now = unlockedCount(order, after);
  if (now <= was) return null;
  return order[now - 1] ?? null;
}

/** Najdalsza odblokowana trasa (ta, ktora gracz ma teraz zrobic). */
export function frontierChallengeId(
  order: readonly string[],
  progress: Progress,
): string | null {
  const count = unlockedCount(order, progress);
  if (count === 0) return null;
  return order[count - 1] ?? null;
}

/** Usuwa postep (przycisk "Resetuj postep" w widoku mapy). */
export function clearProgress(storage: Storage | null = safeStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(PROGRESS_KEY);
  } catch {
    // ignorujemy - brak zapisu to nie blad gry
  }
}
